import React, { useState, useEffect } from 'react'
import {
  Box,
  Text,
  Stack,
  Flex,
  Icon,
  Divider,
  VStack,
  Container,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  HStack,
  Button,
  Badge,
  Skeleton,
  SimpleGrid,
  useToast,
  Spinner,
  Center,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  useBreakpointValue
} from '@chakra-ui/react'
import { FaCircle, FaRobot, FaEye } from 'react-icons/fa'
import DefaultLayout from '../components/layouts/DefaultLayout'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

interface NewsItem {
  id: number
  title: string
  content: string
  summary?: string
  source: string
  category: 'OFFICIAL' | 'NEWS' | 'ANALYSIS' | 'TECHNICAL' | 'EXTERNAL' | 'AI_GENERATED'
  priority: 'HIGH' | 'MEDIUM' | 'LOW'
  author?: string
  imageUrl?: string
  publishedAt?: string
  isAiGenerated: boolean
  aiModel?: string
  isProcessed?: boolean
  tags?: string[]
  viewCount: number
  likeCount: number
  shareCount: number
  createdAt: string
}

const News = () => {
  const { t } = useTranslation()
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null)
  const [news, setNews] = useState<NewsItem[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)

  // 반응형 설정
  const containerMaxW = useBreakpointValue({ base: '100%', md: 'container.xl' })
  const isMobile = useBreakpointValue({ base: true, md: false })
  const fontSize = useBreakpointValue({ base: 'sm', md: 'md' })

  const fetchNews = async (pageNum: number = 1, append: boolean = false) => {
    try {
      setLoading(true)
      const params = new URLSearchParams({
        page: pageNum.toString(),
        limit: '20'
      })

      const response = await fetch(`/api/news?${params}`)
      const result = await response.json()

      if (response.ok && result.data) {
        const newNews = result.data.news
        if (append) {
          setNews(prev => [...prev, ...newNews])
        } else {
          setNews(newNews)
        }
        setHasMore(newNews.length === 20)
      } else {
        throw new Error(result.message || '뉴스를 불러오는데 실패했습니다.')
      }
    } catch (error) {
      toast({
        title: '뉴스 로드 실패',
        description: '뉴스를 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchNews(1, false)
  }, [])

  const handleLoadMore = () => {
    const nextPage = page + 1
    setPage(nextPage)
    fetchNews(nextPage, true)
  }

  const handleViewFullContent = (newsItem: NewsItem) => {
    setSelectedNews(newsItem)
    onOpen()
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'AI_GENERATED': return 'purple'
      case 'OFFICIAL': return 'blue'
      case 'NEWS': return 'green'
      case 'ANALYSIS': return 'orange'
      case 'TECHNICAL': return 'teal'
      default: return 'gray'
    }
  }

  const getCategoryLabel = (category: string) => {
    const categoryLabels: { [key: string]: string } = {
      'AI_GENERATED': 'AI 생성',
      'OFFICIAL': '공식',
      'NEWS': '뉴스',
      'ANALYSIS': '분석',
      'TECHNICAL': '기술',
      'EXTERNAL': '외부'
    }
    return categoryLabels[category] || category
  }

  return (
    <DefaultLayout>
      <Container maxW={containerMaxW} p={0} mt={4}>
        <Box px={{ base: 4, md: 8 }} py={4}>
          <VStack spacing={4} align="stretch" mb={6}>
            <VStack align="start" spacing={1}>
              <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="bold">
                {t('XRP 뉴스')}
              </Text>
              <Text fontSize={{ base: 'sm', md: 'md' }} color="gray.600">
                {t('실시간으로 업데이트되는 XRP 관련 최신 뉴스와 AI 분석 리포트')}
              </Text>
            </VStack>
            
          </VStack>

          {loading && news.length === 0 ? (
            <VStack spacing={4} align="stretch">
              {[...Array(5)].map((_, index) => (
                <Box key={index} p={6} bg="white" borderRadius="lg" shadow="sm">
                  <Skeleton height="20px" mb={3} />
                  <Skeleton height="16px" mb={2} />
                  <Skeleton height="16px" width="80%" />
                </Box>
              ))}
            </VStack>
          ) : (
            <VStack spacing={4} align="stretch">
              {news.map((newsItem) => (
                <Box key={newsItem.id} p={{ base: 4, md: 6 }} bg="white" borderRadius="lg" shadow="sm" _hover={{ shadow: 'md' }}>
                  <VStack spacing={3} align="stretch">
                    {/* 헤더 정보 */}
                    <Flex direction={{ base: 'column', md: 'row' }} align={{ base: 'start', md: 'center' }} justify="space-between" gap={2}>
                      <HStack spacing={2} flexWrap="wrap">
                        <Icon as={FaCircle} color="green.500" boxSize={2} />
                        <Text fontSize="xs" color="gray.500">
                          {moment(newsItem.createdAt).format('YYYY.MM.DD HH:mm')}
                        </Text>
                        <Badge colorScheme={getCategoryColor(newsItem.category)} size="sm">
                          {getCategoryLabel(newsItem.category)}
                        </Badge>
                        {newsItem.isAiGenerated && (
                          <Badge colorScheme="purple" size="sm">
                            <Icon as={FaRobot} mr={1} />
                            AI 생성
                          </Badge>
                        )}
                        {newsItem.isProcessed && (
                          <Badge colorScheme="green" size="sm">
                            ✨ 가공됨
                          </Badge>
                        )}
                      </HStack>
                      {/* 임시 숨김 - 향후 기능 구현 예정 */}
                      {/* <HStack spacing={3} fontSize="xs" color="gray.500">
                        <Text>👁 {newsItem.viewCount}</Text>
                        <Text>❤️ {newsItem.likeCount}</Text>
                        <Text>📤 {newsItem.shareCount}</Text>
                      </HStack> */}
                    </Flex>
                    
                    {/* 제목 */}
                    <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="semibold" color="gray.800">
                      {newsItem.title}
                    </Text>
                    
                    {/* 요약 */}
                    {newsItem.summary && (
                      <Text color="blue.600" fontSize="sm" fontStyle="italic">
                        {newsItem.summary}
                      </Text>
                    )}
                    
                    {/* 내용 미리보기 */}
                    <Text color="gray.600" lineHeight="1.6" fontSize={fontSize}>
                      {newsItem.content.length > (isMobile ? 150 : 300) 
                        ? `${newsItem.content.substring(0, isMobile ? 150 : 300)}...` 
                        : newsItem.content
                      }
                    </Text>
                    
                    {/* 전체보기 버튼 */}
                    {newsItem.content.length > (isMobile ? 150 : 300) && (
                      <Button
                        size="sm"
                        variant="outline"
                        colorScheme="blue"
                        leftIcon={<Icon as={FaEye} />}
                        onClick={() => handleViewFullContent(newsItem)}
                        width="100%"
                      >
                        전체보기
                      </Button>
                    )}
                    
                    {/* 출처 및 태그 */}
                    <Flex direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'start', md: 'center' }} gap={2}>
                      <HStack spacing={2} flexWrap="wrap">
                        <Text fontSize="xs" color="gray.500">
                          출처: {newsItem.source}
                        </Text>
                        {newsItem.author && (
                          <Text fontSize="xs" color="gray.500">
                            | 작성자: {newsItem.author}
                          </Text>
                        )}
                      </HStack>
                      
                      {newsItem.tags && newsItem.tags.length > 0 && (
                        <HStack spacing={1} flexWrap="wrap">
                          {newsItem.tags.slice(0, isMobile ? 2 : 3).map((tag, index) => (
                            <Badge key={index} size="sm" colorScheme="gray" variant="subtle">
                              #{tag}
                            </Badge>
                          ))}
                        </HStack>
                      )}
                    </Flex>
                  </VStack>
                </Box>
              ))}
            </VStack>
          )}

          {hasMore && !loading && (
            <Center mt={8}>
              <Button onClick={handleLoadMore} colorScheme="blue" variant="outline">
                더 보기
              </Button>
            </Center>
          )}

          {loading && news.length > 0 && (
            <Center mt={4}>
              <Spinner size="md" color="blue.500" />
            </Center>
          )}

          <Alert status="info" mt={8} borderRadius="lg">
            <AlertIcon />
            <Box>
              <AlertTitle>{t('뉴스 업데이트')}</AlertTitle>
              <AlertDescription>
                {t('XRP 관련 최신 뉴스는 실시간으로 업데이트됩니다. AI가 생성한 분석 리포트도 매일 제공됩니다.')}
              </AlertDescription>
            </Box>
          </Alert>
        </Box>
      </Container>

      {/* 뉴스 상세보기 Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: '4xl' }}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <VStack align="start" spacing={2}>
              <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="bold">
                {selectedNews?.title}
              </Text>
              <HStack spacing={2} flexWrap="wrap">
                <Badge colorScheme={selectedNews ? getCategoryColor(selectedNews.category) : 'gray'} size="sm">
                  {selectedNews ? getCategoryLabel(selectedNews.category) : ''}
                </Badge>
                {selectedNews?.isAiGenerated && (
                  <Badge colorScheme="purple" size="sm">
                    <Icon as={FaRobot} mr={1} />
                    AI 생성
                  </Badge>
                )}
                <Text fontSize="sm" color="gray.500">
                  {selectedNews ? moment(selectedNews.createdAt).format('YYYY년 MM월 DD일 HH:mm') : ''}
                </Text>
              </HStack>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {selectedNews?.summary && (
                <Box p={4} bg="blue.50" borderRadius="md">
                  <Text color="blue.700" fontSize="sm" fontStyle="italic" fontWeight="medium">
                    {selectedNews.summary}
                  </Text>
                </Box>
              )}
              
              <Text 
                color="gray.700" 
                lineHeight="1.8" 
                fontSize={{ base: 'sm', md: 'md' }}
                whiteSpace="pre-wrap"
              >
                {selectedNews?.content}
              </Text>
              
              <Divider />
              
              <Flex direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'start', md: 'center' }} gap={4}>
                <VStack align="start" spacing={2}>
                  <Text fontSize="sm" color="gray.600">
                    출처: {selectedNews?.source}
                  </Text>
                  {selectedNews?.author && (
                    <Text fontSize="sm" color="gray.600">
                      작성자: {selectedNews.author}
                    </Text>
                  )}
                </VStack>
                
                {/* 임시 숨김 - 향후 기능 구현 예정 */}
                {/* <HStack spacing={4} fontSize="sm" color="gray.500">
                  <Text>👁 {selectedNews?.viewCount}</Text>
                  <Text>❤️ {selectedNews?.likeCount}</Text>
                  <Text>📤 {selectedNews?.shareCount}</Text>
                </HStack> */}
              </Flex>
              
              {selectedNews?.tags && selectedNews.tags.length > 0 && (
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.600">
                    태그:
                  </Text>
                  <HStack spacing={2} flexWrap="wrap">
                    {selectedNews.tags.map((tag, index) => (
                      <Badge key={index} size="sm" colorScheme="blue" variant="subtle">
                        #{tag}
                      </Badge>
                    ))}
                  </HStack>
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onClose}>
              닫기
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Box mb={10} />
    </DefaultLayout>
  )
}

export default News
